import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Col, Container, Row, Table, Breadcrumb, Form } from "react-bootstrap";
import CardHeader from "react-bootstrap/CardHeader";
import { Link, useNavigate, useParams } from "react-router-dom";
import PersonForm from "./components/PersonForm";
import { PersonModel } from "../../models/PersonModel";
import { PersonFile, PersonType } from "../../types/PersonType";
import Loader from "../../components/messages/Loader";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import { OptionType } from "../../types/CommonType";
import CustomSelect from "../../components/form/CustomSelect";
import RodTable from "./components/RodTable";
import { ImageUploadProvider, ImageUploadProviderContext } from "../../providers/ImageUploadProvider";
import ImageUploader from "../../components/ImageUploader";
import InputMask from "react-input-mask";
import clsx from "clsx";
import moment from "moment";
import { useStateList } from "../../hooks/useStateList";
import TranslateTerms from "../../components/TranslateTerms";
import { FileUploadProvider, FileUploadProviderContext } from "../../providers/FileUploadProvider";
import FileUploader from "../../components/FileUploader";
import { FormattedMessage, useIntl } from "react-intl";
import { OptionsContext } from "../../providers/OptionsContextProvider";
import { LANG_CITY, LANG_COMMON_BACK, LANG_COMMON_DATA, LANG_COMMON_FILES, LANG_COMMON_MEDICAL_RECORD_NUMBER, LANG_COMMON_NAME, LANG_COMMON_NO, LANG_COMMON_OPTIONS, LANG_COMMON_OTHER_DATA, LANG_COMMON_YES, LANG_PERSON_AGE, LANG_PERSON_BIRTH_DATE, LANG_PERSON_BLOOD_TYPE, LANG_PERSON_BMI, LANG_PERSON_EYES_COLOR, LANG_PERSON_HAIR_COLOR, LANG_PERSON_HAIR_TYPE, LANG_PERSON_HAS_CHILDREN, LANG_PERSON_HEIGHT, LANG_PERSON_HOBBIES, LANG_PERSON_OCCUPATION, LANG_PERSON_OTHER_INFO, LANG_PERSON_OTHER_INFO_DETAILS, LANG_PERSON_PICTURE, LANG_PERSON_PICTURE_AVATAR, LANG_PERSON_PICTURE_KID, LANG_PERSON_RACE, LANG_PERSON_RH_FACTOR, LANG_PERSON_SCHOOLING, LANG_PERSON_SKIN_COLOR, LANG_PERSON_WEIGHT, LANG_STATE } from "../../lang";
import PairingFilters from "../pairing/components/PairingFilters";
import Error from "../../components/messages/Error";
import mixpanel from "mixpanel-browser";

const PersonViewPage = () => {
    const intl = useIntl();
    const options = useContext(OptionsContext);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [person, setPerson] = useState<PersonType>();
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const { getStateName, getStateShort } = useStateList();
    const [files, setFiles] = useState<PersonFile[] | undefined>([]);

    const [showDraftError, setShowDraftError] = useState(false);
    const [showPairingModal, setShowPairingModal] = useState(false);

    const calculateAge = (birth_date: string | undefined) => {
        if (birth_date === undefined) return "0";
        const moment_birth_date = moment(birth_date, 'DD/MM/YYYY');
        if (moment_birth_date.isValid()) {
            return moment().diff(moment_birth_date, 'years');
        }
        return 0;
    }

    const calculateBmi = (height: string | undefined, weight: string | undefined) => {
        if (weight !== undefined && height !== undefined)
            return (parseFloat(weight) / Math.pow(parseFloat(height), 2)).toFixed(2);

        return undefined
    }

    const loadPerson = () => {
        setLoading(true);
        if (id !== undefined) {
            PersonModel().get(id).then((resp) => {
                if (typeof resp !== "boolean") {
                    formik.setFieldValue("schooling", resp.schooling);
                    formik.setFieldValue("has_children", resp.has_children);
                    formik.setFieldValue("hobbies", resp.hobbies);
                    setPerson(resp);
                    setFiles(resp.files);
                }
                setLoading(false);
            });
        }

    }

    const loadFiles = () => {
        setLoading(true);
        if (id !== undefined) {
            PersonModel().get(id).then((resp) => {
                if (typeof resp !== "boolean") {
                    setFiles(resp.files);
                }
                setLoading(false);
            });
        }

    }

    const schema = yup.object().shape({
        schooling: yup.string().nullable(),
        has_children: yup.boolean().nullable(),
        hobbies: yup.string().nullable(),
    });
    const formik = useFormik({
        initialValues: {
            schooling: "",
            has_children: undefined,
            hobbies: ""
        },
        validationSchema: schema,
        onSubmit: () => { },
        validateOnMount: false
    });

    useEffect(() => {
        loadPerson();
    }, [id]);

    return (
        <>
            {loading && (<Loader fullscreen={true} />)}
            {person && (
                <div className="bg-pattern">
                    {showDraftError && (
                        <Error message={intl.formatMessage({
                            id: "person.error.draft",
                            defaultMessage: "Por favor, complete o cadastro para continuar!",
                            description: "Mensagem de erro ao tentar parear com cadastro incompleto."
                        })} show={showDraftError} onHide={() => setShowDraftError(false)} />
                    )}
                    <PairingFilters
                        show={showPairingModal}
                        person={person}
                        onHide={() => setShowPairingModal(false)}
                        onSave={(filters) => {
                            try {
                                mixpanel.track("New Matching", {
                                    "Person ID": person.id
                                });
                            } catch (error) {
                                console.error("Error tracking mixpanel event:", error);
                            }
                            navigate(`/admin/pairing/${person.id}/add?${new URLSearchParams(filters).toString()}`);
                        }} />
                    <Container style={{ 'paddingBottom': '200px' }}>
                        <Row>
                            <Breadcrumb>
                                {person.type === "R" && (
                                    <>
                                        <Breadcrumb.Item><Link to={'/admin/receivers'}><TranslateTerms term="receptor" /></Link></Breadcrumb.Item>
                                    </>
                                )}
                                {person.type === "D" && (
                                    <>
                                        <Breadcrumb.Item><Link to={'/admin/donors'}><TranslateTerms term="donor" /></Link></Breadcrumb.Item>
                                    </>
                                )}
                                <Breadcrumb.Item active>{person.name}</Breadcrumb.Item>
                            </Breadcrumb>
                        </Row>
                        <Row>
                            <Col lg={{ offset: 0 }}>
                                <Card>
                                    <CardHeader className={'d-flex'}>
                                        <Card.Title>{intl.formatMessage(LANG_COMMON_DATA)}</Card.Title>
                                    </CardHeader>
                                    <Card.Body>
                                        <Form>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <h5 className="person-name">{person.name}</h5>
                                                </div>
                                            </div>
                                            <Row>
                                                <Col lg={3}>
                                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                                        <ImageUploadProvider url={`/api/person/${person.id}/photo`} onChange={() => { }}>
                                                            <ImageUploadProviderContext.Consumer>
                                                                {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, download }) => (
                                                                    <ImageUploader
                                                                        photoLabel={`${intl.formatMessage(LANG_PERSON_PICTURE)}`}
                                                                        hasUploadError={hasUploadError}
                                                                        hasDownloadError={hasDownloadError}
                                                                        download={download}
                                                                        loading={loading}
                                                                        edit={false}
                                                                        height={175}
                                                                        width={145}
                                                                        url={url}
                                                                        onChange={onChange}
                                                                        onRemove={onRemove}
                                                                        uploadUrl={`/api/person/${person.id}/photo`}
                                                                        removeUrl={""}
                                                                        showQuality={true}
                                                                        faceData={(person.face_details !== null && person.face_details !== undefined) ? JSON.parse(person.face_details) : undefined}
                                                                    />
                                                                )}
                                                            </ImageUploadProviderContext.Consumer>
                                                        </ImageUploadProvider>
                                                    </Form.Group>
                                                </Col>
                                                <Col lg={3}>
                                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                                        <ImageUploadProvider url={`/api/person/${person.id}/photo/child`} onChange={() => { }}>
                                                            <ImageUploadProviderContext.Consumer>
                                                                {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, download }) => (
                                                                    <ImageUploader
                                                                        photoLabel={`${intl.formatMessage(LANG_PERSON_PICTURE_KID)}`}
                                                                        hasUploadError={hasUploadError}
                                                                        hasDownloadError={hasDownloadError}
                                                                        download={download}
                                                                        loading={loading}
                                                                        edit={false}
                                                                        height={175}
                                                                        width={145}
                                                                        url={url}
                                                                        onChange={onChange}
                                                                        onRemove={onRemove}
                                                                        uploadUrl={`/api/person/${person.id}/photo/child`}
                                                                        removeUrl={""} />
                                                                )}
                                                            </ImageUploadProviderContext.Consumer>
                                                        </ImageUploadProvider>
                                                    </Form.Group>
                                                </Col>
                                                {person.photo_avatar_file_id && (
                                                    <Col lg={3}>
                                                        <Form.Group className="mb-3" controlId="formBasicEmail">
                                                            <ImageUploadProvider url={`/api/person/${person.id}/photo/avatar`} onChange={() => { }}>
                                                                <ImageUploadProviderContext.Consumer>
                                                                    {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, download }) => (
                                                                        <ImageUploader
                                                                            photoLabel={`${intl.formatMessage(LANG_PERSON_PICTURE_AVATAR)}`}
                                                                            hasUploadError={hasUploadError}
                                                                            hasDownloadError={hasDownloadError}
                                                                            download={download}
                                                                            loading={loading}
                                                                            edit={false}
                                                                            height={175}
                                                                            width={145}
                                                                            url={url}
                                                                            onChange={onChange}
                                                                            onRemove={onRemove}
                                                                            uploadUrl={`/api/person/${person.id}/photo/avatar`}
                                                                            removeUrl={""} />
                                                                    )}
                                                                </ImageUploadProviderContext.Consumer>
                                                            </ImageUploadProvider>
                                                        </Form.Group>
                                                    </Col>
                                                )}
                                            </Row>
                                            <Row>
                                                <Col lg={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_COMMON_NAME)}</Form.Label>
                                                        <div className="view-data">{person.name}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col lg={3}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_COMMON_MEDICAL_RECORD_NUMBER)}</Form.Label>
                                                        <div className="view-data">{person.medical_record_number}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col lg={3}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Data do Cadastro</Form.Label>
                                                        <div className="view-data">{moment(person.created_at).format("DD/MM/YYYY")}</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_BLOOD_TYPE)} ({intl.formatMessage(LANG_PERSON_RH_FACTOR)})</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.blood_type, PersonModel().getBloodTypes())?.label} ({person.rh_factor === "P" ? "Positivo" : "Negativo"})</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_RACE)}</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.race, options.personOptions?.getRaceTypes())?.label}</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_HEIGHT)}</Form.Label>
                                                        <div className="view-data">{person.height_number}m</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_WEIGHT)}</Form.Label>
                                                        <div className="view-data">{person.weight}kg</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_BMI)}</Form.Label>
                                                        <div className="view-data">{calculateBmi(person.height_number, person.weight)}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col lg={4}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_BIRTH_DATE)}</Form.Label>
                                                        <div className="view-data">{person.birth_date}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col lg={2}>
                                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_AGE)}</Form.Label>
                                                        <div className="view-data">{calculateAge(person.birth_date)} anos</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_SKIN_COLOR)}</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.skin_color, options.personOptions?.getSkinColors())?.label}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_HAIR_COLOR)}</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.hair_color, options.personOptions?.getHairColors())?.label}</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_HAIR_TYPE)}</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.hair_type, options.personOptions?.getHairTypes())?.label}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_PERSON_EYES_COLOR)}</Form.Label>
                                                        <div className="view-data">{PersonModel().getOption(person.eyes_color, options.personOptions?.getEyesColors())?.label}</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_STATE)}</Form.Label>
                                                        <div className="view-data">{getStateName(person.state_id)}</div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{intl.formatMessage(LANG_CITY)}</Form.Label>
                                                        <div className="view-data">{person.city?.name}</div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg={12}>
                                                    <Form.Label>
                                                        <FormattedMessage
                                                            {...LANG_PERSON_OTHER_INFO}
                                                        />
                                                        <small>
                                                            {" "}
                                                            <FormattedMessage
                                                                {...LANG_PERSON_OTHER_INFO_DETAILS}
                                                            />
                                                        </small></Form.Label>
                                                    <div className="view-data">{person.notes}</div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {person.type === "D" && (
                                    <div className="my-4">
                                        {id !== undefined && (<RodTable personId={parseInt(id)} person={person} edit={false} />)}
                                    </div>
                                )}
                                <Card className="my-4">
                                    <CardHeader className={'d-flex'}>
                                        <Card.Title>{intl.formatMessage(LANG_COMMON_OTHER_DATA)}</Card.Title>
                                    </CardHeader>
                                    <Card.Body>
                                        <Row>
                                            <Col>
                                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                                    <Form.Label>{intl.formatMessage(LANG_PERSON_SCHOOLING)}</Form.Label>
                                                    <div className="view-data">{PersonModel().getOption(person.schooling, options.personOptions?.getSchooling())?.label}</div>
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                                    <Form.Label>{intl.formatMessage(LANG_PERSON_HAS_CHILDREN)}</Form.Label>
                                                    <Row>
                                                        <Col lg={3}>
                                                            <div className="view-data">{(person.has_children) ? intl.formatMessage(LANG_COMMON_YES) : intl.formatMessage(LANG_COMMON_NO)}</div>
                                                        </Col>
                                                    </Row>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                                    <Form.Label>{intl.formatMessage(LANG_PERSON_OCCUPATION)}</Form.Label>
                                                    <div className="view-data">{person.occupation}</div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                                    <Form.Label>{intl.formatMessage(LANG_PERSON_HOBBIES)}</Form.Label>
                                                    <div className="view-data">{person.hobbies}</div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={{ span: 3 }}>
                                <Card>
                                    <CardHeader>
                                        <Card.Title>{intl.formatMessage(LANG_COMMON_FILES)}</Card.Title>
                                    </CardHeader>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-sm-12">
                                                {files?.map((personFile, index) => (
                                                    <>
                                                        <FileUploadProvider url={`/api/person/${id}/file/${personFile.file_id}`} onChange={loadFiles} key={personFile.file_id}>
                                                            <FileUploadProviderContext.Consumer>
                                                                {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, download, uploadErrorMessage }) => (
                                                                    <FileUploader
                                                                        hasUploadError={hasUploadError}
                                                                        hasDownloadError={false}
                                                                        uploadErrorMessage={uploadErrorMessage}
                                                                        download={download}
                                                                        loading={loading}
                                                                        url={url}
                                                                        edit={false}
                                                                        onChange={onChange}
                                                                        onRemove={onRemove}
                                                                        uploadUrl={``}
                                                                        removeUrl={`/api/person/${id}/file/${personFile.file_id}`}
                                                                        remove={true}
                                                                        name={personFile.file.name}
                                                                    />
                                                                )}
                                                            </FileUploadProviderContext.Consumer>
                                                        </FileUploadProvider>
                                                        <hr />
                                                    </>
                                                ))}
                                            </div>
                                            <div className="col-sm-12">
                                                <FileUploadProvider url={undefined} onChange={() => { }} onDone={loadFiles}>
                                                    <FileUploadProviderContext.Consumer>
                                                        {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, download, uploadErrorMessage }) => (
                                                            <FileUploader
                                                                hasUploadError={hasUploadError}
                                                                hasDownloadError={false}
                                                                uploadErrorMessage={uploadErrorMessage}
                                                                download={download}
                                                                loading={loading}
                                                                url={url}
                                                                onChange={onChange}
                                                                onRemove={onRemove}
                                                                uploadUrl={`/api/person/${person.id}/file`}
                                                                removeUrl={""} />
                                                        )}
                                                    </FileUploadProviderContext.Consumer>
                                                </FileUploadProvider>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="my-4">
                                    <CardHeader>
                                        <Card.Title>{intl.formatMessage(LANG_COMMON_OPTIONS)}</Card.Title>
                                    </CardHeader>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-md-12">
                                                <Button
                                                    variant="outline-light"
                                                    size={'lg'}
                                                    style={{ "width": "100%" }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (!person.is_draft) {
                                                            setShowPairingModal(true);
                                                        } else {
                                                            setShowDraftError(true);
                                                        }
                                                    }}
                                                    className="btn-custom btn-custom-light btn-custom-small text-uppercase w-100 mb-3">
                                                    <i className="icon icon-pairing">
                                                        <svg version="1.1" id="Camada_1" xmlns="http://www.w3.org/2000/svg"
                                                            x="0px" y="0px"
                                                            viewBox="0 0 20.19 19.64">
                                                            <g>
                                                                <path className="st0" d="M17.13,5.35L15.08,7.4l-0.03,0.04c-0.03,0.03-0.04,0.04-0.06,0.08c-0.34,0.48-0.05,1.29,0.58,1.4
                                c0.28,0.05,0.57-0.05,0.78-0.25l3.58-3.58c0.17-0.18,0.27-0.43,0.26-0.68c-0.02-0.22-0.11-0.42-0.26-0.58l-3.58-3.58
                                c-0.03-0.03-0.04-0.04-0.07-0.07c-0.39-0.31-1.04-0.21-1.32,0.22c-0.21,0.34-0.16,0.81,0.13,1.11l2.05,2.05H0.89
                                C0.55,3.6,0.23,3.76,0.08,4.07c-0.18,0.36-0.07,0.84,0.25,1.09c0.16,0.13,0.36,0.17,0.56,0.2L17.13,5.35L17.13,5.35z M3.05,16.09
                                h16.24c0.33-0.04,0.66-0.2,0.81-0.51c0.08-0.16,0.07-0.21,0.09-0.39c-0.01-0.07-0.02-0.13-0.02-0.2c-0.04-0.13-0.09-0.25-0.17-0.36
                                c-0.13-0.16-0.31-0.27-0.5-0.31c-0.06-0.02-0.13-0.02-0.2-0.02H3.05l2.05-2.05c0,0,0.27-0.35,0.26-0.68c0-0.04,0-0.06-0.01-0.1
                                c-0.08-0.49-0.64-0.85-1.13-0.71c-0.1,0.03-0.19,0.07-0.27,0.13c-0.04,0.03-0.08,0.06-0.12,0.1l-3.58,3.58
                                c-0.03,0.03-0.04,0.04-0.07,0.08c-0.25,0.31-0.25,0.8,0,1.12c0.03,0.03,0.04,0.04,0.07,0.08l3.58,3.58
                                c0.03,0.03,0.04,0.04,0.08,0.07c0.47,0.38,1.33,0.12,1.44-0.55c0.05-0.28-0.05-0.57-0.25-0.78L3.05,16.09L3.05,16.09z"/>
                                                            </g>
                                                        </svg>
                                                    </i> Iniciar Busca
                                                </Button>
                                            </div>
                                            <div className="col-md-6">
                                                {person.type === "R" && (
                                                    <Link to="/admin/receivers">
                                                        <Button variant="outline-light" size={'lg'} style={{ "width": "100%" }} className="btn-custom bg-custom-gradient btn-custom-small text-uppercase w-100">{intl.formatMessage(LANG_COMMON_BACK)}</Button>
                                                    </Link>
                                                )}
                                                {person.type === "D" && (
                                                    <Link to="/admin/donors">
                                                        <Button variant="outline-light" size={'lg'} style={{ "width": "100%" }} className="btn-custom bg-custom-gradient btn-custom-small text-uppercase w-100">{intl.formatMessage(LANG_COMMON_BACK)}</Button>
                                                    </Link>
                                                )}

                                            </div>
                                            <div className="col-md-6">
                                                <Link to={`/admin/person/${person.id}/edit`}>
                                                    <Button variant="outline-light" size={'lg'} style={{ "width": "100%" }} className="btn-custom btn-custom-light btn-custom-small text-uppercase w-100"><i className="bi bi-pencil icon" /> Editar</Button>
                                                </Link>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            )}
        </>
    );
}

export default PersonViewPage;